/**
 * Genera el PDF real de una credencial (frente, y dorso si el diseño es
 * doble cara), tamaño físico exacto ISO/IEC 7810 ID-1 (85.60 x 53.98mm).
 * Todo pasa en el navegador: renderiza LicenseCard fuera de pantalla en
 * modo impresión física (paraImpresionFisica=true — nunca pinta el fondo
 * preimpresa, ver LicenseCard.jsx), lo captura con html2canvas, arma el
 * PDF con jsPDF, y devuelve un Blob. Nunca hace ningún POST ni guarda
 * nada en el servidor — la persistencia (confirmar-impresion) es un paso
 * aparte, a cargo de quien use este Blob.
 *
 * ⚠️ generarPDFLicencia() e imprimirSilenciosamente() (más abajo) ya NO
 * las usa el flujo principal de impresión de Padrón — ese pasó a
 * ZonaImprimible.jsx + CSS @media print (impresión directa de la página,
 * sin generar ningún PDF intermedio). Quedan acá a propósito, no son
 * código muerto para limpiar: sirven para un futuro caso de uso distinto
 * (ej. mandar el PDF por mail), donde sí hace falta el archivo en sí y no
 * solo mandarlo a la impresora.
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import LicenseCard from '../components/LicenseCard';

const ANCHO_MM = 85.6;
const ALTO_MM = 53.98;
const MM_POR_PULGADA = 25.4;
const DPI_OBJETIVO = 300;

// Ancho (en px CSS) al que se renderiza el card fuera de pantalla antes de
// capturarlo. Es un valor arbitrario pero conocido — el scale de
// html2canvas se calcula a partir de él, no al revés, así que cualquier
// valor razonable funciona igual de bien.
const ANCHO_RENDER_PX = 560;

// scale = píxeles reales que necesitamos para ~300dpi de impresión,
// divididos por los píxeles CSS a los que efectivamente renderizamos el
// card. No es un número fijo adivinado: sale de la proporción entre el
// tamaño físico real de la tarjeta y el ancho de render elegido arriba.
function calcularScale() {
  const anchoObjetivoPx = (ANCHO_MM / MM_POR_PULGADA) * DPI_OBJETIVO;
  return anchoObjetivoPx / ANCHO_RENDER_PX;
}

// Exportada: la reutiliza también ZonaImprimible/Padron.jsx para esperar
// a que las imágenes de la zona imprimible carguen antes de window.print().
export function esperarImagenes(contenedor) {
  const imgs = Array.from(contenedor.querySelectorAll('img'));
  return Promise.all(
    imgs.map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    })
  );
}

// Precarga URLs que se usan como CSS background-image (fondo del diseño),
// NO como <img>: esperarImagenes() no las cubre porque un background-image
// no genera ningún nodo <img>. Al bajarlas + decodificarlas antes de
// window.print(), el background-image con la misma URL resuelve desde caché
// al instante y no queda en blanco en el diálogo de impresión. onerror
// también resuelve — mismo criterio que esperarImagenes, no bloquea si una
// falla.
export function precargarImagenesFondo(urls) {
  return Promise.all(
    (urls || []).filter(Boolean).map((url) =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = () => (img.decode ? img.decode().then(resolve, resolve) : resolve());
        img.onerror = resolve;
        img.src = url;
      })
    )
  );
}

// Monta LicenseCard fuera de pantalla (position:fixed, corrido de la vista
// pero igual de renderizado/con layout real — es la técnica estándar para
// que html2canvas pueda capturarlo), espera a que las imágenes carguen, lo
// captura, y limpia todo antes de devolver el PNG en base64.
async function capturarCara(contribuyente, diseno, cara) {
  const contenedor = document.createElement('div');
  contenedor.style.position = 'fixed';
  contenedor.style.left = '-10000px';
  contenedor.style.top = '0';
  contenedor.style.width = `${ANCHO_RENDER_PX}px`;
  document.body.appendChild(contenedor);

  const root = createRoot(contenedor);
  await new Promise((resolve) => {
    root.render(
      React.createElement(LicenseCard, { contribuyente, diseno, cara, paraImpresionFisica: true })
    );
    // Doble rAF: garantiza que React ya pintó el DOM antes de seguir
    // (un solo rAF puede alcanzar en la práctica, pero dos es lo seguro).
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });

  try {
    const cardEl = contenedor.querySelector('.license-card') || contenedor;
    await esperarImagenes(cardEl);

    const canvas = await html2canvas(cardEl, {
      scale: calcularScale(),
      useCORS: true,
      backgroundColor: '#ffffff',
    });

    return canvas.toDataURL('image/png');
  } finally {
    root.unmount();
    document.body.removeChild(contenedor);
  }
}

/**
 * @param {object} contribuyente - mismo shape que ya usa PadronPreviewModal
 * @param {object} diseno - mismo shape que ya usa PadronPreviewModal (el
 *   diseño activo resuelto, con layout_frente/layout_dorso si corresponde)
 * @returns {Promise<Blob>} el PDF como Blob, listo para descargar o subir
 */
export async function generarPDFLicencia(contribuyente, diseno) {
  const esDobleCara = diseno.tipo_impresion === 'doble_cara';

  const imagenFrente = await capturarCara(contribuyente, diseno, 'frente');

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: [ANCHO_MM, ALTO_MM],
  });
  doc.addImage(imagenFrente, 'PNG', 0, 0, ANCHO_MM, ALTO_MM);

  if (esDobleCara) {
    const imagenDorso = await capturarCara(contribuyente, diseno, 'dorso');
    doc.addPage([ANCHO_MM, ALTO_MM], 'landscape');
    doc.addImage(imagenDorso, 'PNG', 0, 0, ANCHO_MM, ALTO_MM);
  }

  return doc.output('blob');
}

// Margen para que el visor de PDF interno (PDFium en Chrome) termine de
// renderizar el contenido del iframe antes de llamar a print() — sin esto,
// en algunos navegadores print() se dispara antes de que el visor termine
// de cargar y sale una hoja en blanco. Si en la práctica sigue saliendo en
// blanco, subir este valor primero antes de tocar cualquier otra cosa.
const MARGEN_RENDER_PDF_MS = 500;

// No hay ninguna señal de "cuándo terminó de imprimir" — se sacó a
// propósito (ver comentario de la función). Esto es solo higiene de
// memoria: en algún momento hay que sacar el iframe del DOM y liberar el
// blob URL, así que se hace después de un margen bien generoso, sin que
// tenga nada que ver con cuándo la persona ve o cierra el diálogo de
// impresión del sistema (que puede quedar abierto mucho más o mucho
// menos que esto).
const LIMPIEZA_DIFERIDA_MS = 120000;

/**
 * Dispara la impresión de un PDF sin ninguna ventana visible: lo carga en
 * un <iframe> oculto y llama a print() — y ya. No espera ninguna señal de
 * "cuándo terminó" (no hay afterprint, no hay timeout de seguridad, no
 * hay nada que esperar): el diálogo nativo de impresión del sistema tapa
 * toda la pantalla mientras está abierto, así que quien llama puede
 * simplemente mostrar el siguiente paso de inmediato — en la práctica
 * queda oculto detrás del diálogo hasta que la persona lo cierra.
 *
 * Sigue vigente la limitación de Chrome sobre el estilo del iframe: no
 * imprime contenido de iframes con `display:none`, y uno de 0x0px no es
 * confiable cuando el contenido es un PDF (el visor interno necesita
 * espacio real para inicializarse) — por eso el iframe queda fuera de
 * pantalla por posición, con un tamaño real, nunca oculto por
 * display/tamaño cero.
 *
 * @param {Blob} blob - el PDF ya generado (generarPDFLicencia)
 */
export function imprimirSilenciosamente(blob) {
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');

  iframe.style.position = 'fixed';
  iframe.style.top = '-10000px';
  iframe.style.left = '-10000px';
  iframe.style.width = '800px';
  iframe.style.height = '600px';
  iframe.style.border = '0';

  iframe.onload = () => {
    setTimeout(() => {
      try {
        iframe.contentWindow.print();
      } catch (err) {
        console.error('Error al disparar la impresión:', err);
      }
    }, MARGEN_RENDER_PDF_MS);
  };

  iframe.src = url;
  document.body.appendChild(iframe);

  setTimeout(() => {
    URL.revokeObjectURL(url);
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  }, LIMPIEZA_DIFERIDA_MS);
}
